import { Observable } from 'rxjs/Observable'
import { Map, List, } from 'immutable'
import {
  checkStatus,
  json,
  RxConversationAPI,
  ConversationAPI,
  RxConversationClientAPI,
  RxAnswersAPI,
  MessagingAPI,
  RxMessagingAPI,
} from '@driftt/dapper'
import { parsePagedData, PagedData } from 'utils/paging'
import { encodeQueryData } from 'utils/Helpers'
import {
  parseConversation,
  parseRoutingAuditLogEntry,
  parseEnrichedConversations,
} from './models'
import * as DemoData from './demodata'

// Given paginated, enriched conversations:
// 1. Separate pagintation metadata from enriched conversations.
// 2. Parse the conversations into a normalized structure where each key
//    contains values for the expanded models (conversations, endUsers,
//    enrichmentData).
// 3. Reconstruct a normalize payload, nesting the pagination metadata under
//    the `paginatedConversations` key, colocating the metadata with the models
//    for which it is associated.
const normalizePaginatedEnrichedConversations = (paginatedEnrichedConversations) => {
  const {
    data: enrichedConversations,
    ...paginationMetadata,
  } = paginatedEnrichedConversations.toJS()

  const parsed = parseEnrichedConversations(enrichedConversations)

  const conversations = parsed.get('conversations')
  const endUsers = parsed.get('endUsers')
  const enrichedCompanyDataByEndUserId = parsed.get('enrichedCompanyDataByEndUserId')

  return {
    paginatedConversations: PagedData({
      data: conversations,
      ...paginationMetadata,
    }),
    endUsers,
    enrichedCompanyDataByEndUserId,
  }
}

export const demoApi = {
  fetchConversationsByInbox({ inboxId }) {
    const conversations = List(DemoData.conversations)
      .map(conversation =>
        parseConversation(conversation)
          .set('inboxId', inboxId))

    const paginationMetadata = {
      hasMore: false,
      total : 0,
      cursor: 1516982801425
    }

    return Observable.of(PagedData({ ...paginationMetadata, data: conversations }))
  },

  fetchEnrichedConversationsByInbox({ inboxId, ...query }) {
    return Observable.of(DemoData.makeEnrichedPaginatedConversations({ inboxId, query }))
      .map(normalizePaginatedEnrichedConversations)
  },
}

export const api = {
  fetch(conversationId) {
    return RxConversationAPI.get(`/conversations/${conversationId}`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parseConversation)
  },

  bulkFetch(conversationIds) {
    return RxConversationAPI.get(`/conversations/find`, { ids: conversationIds })
      .checkStatus()
      .retryWithBackoff()
      .mapResponse((data) => Map(data).map(parseConversation))
  },

  create(message) {
    return RxMessagingAPI.post("/messages", message)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parseConversation)
  },

  patch(id, update) {
    return RxConversationAPI
      .patch(`/conversations/${id}`, update)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(parseConversation)
  },

  fetchConversationsForEndUser(endUserId, { limit = 4, onlyInboxVisible = true } = {}) {
    return ConversationAPI
      .get(`/conversations/end_users/${ endUserId }`, { limit, onlyInboxVisible })
      .then(checkStatus)
      .then(json)
  },

  fetchConversationsCountForEndUserId(endUserId) {
    return RxConversationAPI
      .get(`/conversations/end_users/${endUserId}/counts`)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(response => response)

  },

  sendConversationTranscript({ conversationId, email }) {
    return RxConversationAPI
      .post(`/conversations/${conversationId}/transcript?${encodeQueryData({ email })}`)
      .checkStatus()
  },

  fetchConversationsByInbox({ inboxId, ...query }) {
    return RxConversationAPI
      .get(`/inboxes/${inboxId}/conversations?${encodeQueryData(query)}`, undefined, { headers: { 'x-version': 3 }})
      .checkStatus()
      .retryWithBackoff()
      .map(parsePagedData(conversations => {
        return List(conversations).map(parseConversation)
      }))
  },

  fetchBulkConversationEnrichmentData(convoEndUser) {
    return RxConversationClientAPI
      .post('/data/enrichment', convoEndUser)
      .checkStatus()
      .retryWithBackoff()
      .mapResponse(response => response)
  },

  fetchEnrichedConversationsByInbox({ inboxId, ...query }) {
    // Each of these fields will be expanded into fully hydrated models in the
    // nested response payload
    const expandedQuery = { ...query, expand: ['END_USER', 'COMPANY_ENRICHMENT']}
    return RxConversationClientAPI
      .get(`/data/inboxes/${inboxId}/query?${encodeQueryData(expandedQuery)}`)
      .checkStatus()
      .retryWithBackoff()
      .map(parsePagedData())
      .map(normalizePaginatedEnrichedConversations)
  },

  buttonAction(conversationId, buttonActionBody) {
    return RxConversationAPI
    .post(`/conversations/${conversationId}/button-action`, buttonActionBody)
    .checkStatus()
    .retryWithBackoff()
  },

  fetchRoutingAuditLog({ conversationId, routingGroupId }) {
    return RxConversationAPI
      .get(`/routing-audit-log/${conversationId}/${routingGroupId}`)
      .checkStatus()
      .mapResponse(data => data.conversationRoutingEntries.map(parseRoutingAuditLogEntry))
  },

  setConversationLimit(limitEnabled, limitValue) {
    return RxConversationAPI
      .post(`/conversations/conversation-limit`, { enabled: limitEnabled, limit: limitValue })
      .checkStatus()
  },

  fetchSuggestedArticles(messageText) {
    return RxAnswersAPI.post('/answers/suggestions?limit=3', { lastMessageText: messageText })
      .checkStatus()
      .mapResponse(response => response)
  },

  fetchGlobalAutoCloseConfiguration() {
    return RxConversationAPI
      .get(`/conversations/autoclose/config`)
      .checkStatus()
      .mapResponse(({ enabled, timeInSeconds, filters }) => ({
        isEnabled: enabled,
        timeout: timeInSeconds * 1000, // System expects milliseconds
        filters
      }))
  },

  updateGlobalAutoCloseConfiguration({ isEnabled, timeout, filters }) {
    return RxConversationAPI
      .patch(`/conversations/autoclose/config`, {
        enabled: isEnabled,
        timeout: timeout / 1000, // API expects seconds
        filters
      })
      .checkStatus()
  },

  sendLeanDataIntegrationEmail() {
    return ConversationAPI
      .post(`/integration/test/leandata/authorization`)
  }
}

export const createConversation = (message) => {
  return MessagingAPI.post("/messages", message)
    .then(json)
}

export const getIframelyVideoEmbed = (url) => {
  return ConversationAPI.post(`/url_validation/playable_video?${encodeQueryData({ url })}`)
    .then(json)
}

export default api



// WEBPACK FOOTER //
// ./src/scripts/app/modules/conversation/api.js